import React, { useState, useEffect } from 'react';
import { 
  CalendarIcon,
  PlusIcon,
  MapPinIcon,
  ClockIcon,
  UserGroupIcon,
  CheckCircleIcon,
  XMarkIcon,
  QuestionMarkCircleIcon,
  PencilIcon,
  EllipsisVerticalIcon,
  ChevronDownIcon
} from '@heroicons/react/24/outline';
import { CalendarIcon as CalendarSolidIcon } from '@heroicons/react/24/solid';
import intranetApi from '../../services/intranetApi';
import CreateEvent from './CreateEvent';
import api from '../../utils/api';
import { getIconComponent } from '../../utils/iconMapping';

const CompanyEvents = () => {
  const [events, setEvents] = useState([]);
  const [eventTypes, setEventTypes] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [view, setView] = useState('upcoming'); // 'upcoming', 'calendar', 'all'
  const [rsvpLoading, setRsvpLoading] = useState({});
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [dropdownOpen, setDropdownOpen] = useState({});
  
  // Attendees state
  const [eventAttendees, setEventAttendees] = useState({});
  const [attendeesLoading, setAttendeesLoading] = useState({});
  const [showAttendees, setShowAttendees] = useState({});
  
  // Persistent RSVP state to survive tab switches
  const [userRsvpUpdates, setUserRsvpUpdates] = useState({});

  useEffect(() => {
    fetchEvents();
    fetchEventTypes();
    fetchCurrentUser();
  }, [view]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.dropdown-container')) {
        setDropdownOpen({});
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const fetchCurrentUser = async () => {
    try {
      const response = await api.get('/auth/me');
      setCurrentUser(response.data.user);
    } catch (err) {
      console.error('Error fetching current user:', err);
    }
  };

  const fetchEvents = async () => {
    try {
      setLoading(true);
      setError(null);

      let response;
      if (view === 'upcoming') {
        response = await intranetApi.events.getUpcomingEvents();
      } else if (view === 'calendar') {
        response = await intranetApi.events.getCalendarEvents();
      } else {
        response = await intranetApi.events.getAllEvents();
      }

      let events = response.data.events || [];
      
      // Apply persistent RSVP updates to the fetched events
      events = events.map(event => {
        const rsvpUpdate = userRsvpUpdates[event.id];
        if (rsvpUpdate) {
          return {
            ...event,
            user_rsvp_status: rsvpUpdate.status,
            attending_count: rsvpUpdate.attending_count,
            maybe_count: rsvpUpdate.maybe_count,
            declined_count: rsvpUpdate.declined_count
          };
        }
        return event;
      });

      setEvents(events);
    } catch (err) {
      console.error('Error fetching events:', err);
      setError('Failed to load events. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const fetchEventTypes = async () => {
    try {
      const response = await intranetApi.config.getEventTypes();
      setEventTypes(response.data.event_types || []);
    } catch (err) {
      console.error('Error fetching event types:', err);
    }
  };

  const handleRSVP = async (eventId, status) => {
    try {
      setRsvpLoading(prev => ({ ...prev, [eventId]: true }));
      
      await intranetApi.events.rsvpToEvent(eventId, { rsvp_status: status });
      
      // Find the current event to calculate new counts
      const currentEvent = events.find(event => event.id === eventId);
      if (!currentEvent) return;

      // Calculate new counts
      const newCounts = {
        attending_count: status === 'attending' 
          ? (currentEvent.attending_count || 0) + (currentEvent.user_rsvp_status === 'attending' ? 0 : 1)
          : currentEvent.user_rsvp_status === 'attending' 
            ? Math.max(0, (currentEvent.attending_count || 0) - 1)
            : currentEvent.attending_count,
        maybe_count: status === 'maybe'
          ? (currentEvent.maybe_count || 0) + (currentEvent.user_rsvp_status === 'maybe' ? 0 : 1)
          : currentEvent.user_rsvp_status === 'maybe'
            ? Math.max(0, (currentEvent.maybe_count || 0) - 1)
            : currentEvent.maybe_count,
        declined_count: status === 'declined'
          ? (currentEvent.declined_count || 0) + (currentEvent.user_rsvp_status === 'declined' ? 0 : 1)
          : currentEvent.user_rsvp_status === 'declined'
            ? Math.max(0, (currentEvent.declined_count || 0) - 1)
            : currentEvent.declined_count
      };

      // Store in persistent RSVP updates
      setUserRsvpUpdates(prev => ({
        ...prev,
        [eventId]: {
          status,
          ...newCounts
        }
      }));
      
      // Update the event in the local state
      setEvents(prev => prev.map(event => {
        if (event.id === eventId) {
          return {
            ...event,
            user_rsvp_status: status,
            ...newCounts
          };
        }
        return event;
      }));

      // Clear attendees cache for this event so it gets refetched with updated data
      setEventAttendees(prev => {
        const updated = { ...prev };
        delete updated[eventId];
        return updated;
      });

      // If attendees list is currently showing, refetch it
      if (showAttendees[eventId]) {
        await fetchEventAttendees(eventId, true);
      }
    } catch (err) {
      console.error('Error updating RSVP:', err);
    } finally {
      setRsvpLoading(prev => ({ ...prev, [eventId]: false }));
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    
    try {
      let datePart;
      if (dateString.includes('T')) {
        datePart = dateString.split('T')[0];
      } else if (dateString.includes(' ')) {
        datePart = dateString.split(' ')[0];
      } else {
        datePart = dateString;
      }
      
      const [year, month, day] = datePart.split('-');
      const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
      
      return date.toLocaleDateString('en-US', {
        weekday: 'long',
        month: 'long',
        day: 'numeric',
        year: 'numeric'
      });
    } catch (error) {
      console.error('Error formatting date:', error, dateString);
      return dateString;
    }
  };

  const formatTime = (dateString) => {
    if (!dateString) return '';
    
    try {
      let timePart;
      if (dateString.includes('T')) {
        timePart = dateString.split('T')[1];
      } else if (dateString.includes(' ')) {
        timePart = dateString.split(' ')[1];
      } else {
        return dateString;
      }
      
      const timeOnly = timePart ? timePart.slice(0, 5) : '';
      const [hours, minutes] = timeOnly.split(':');
      
      const date = new Date();
      date.setHours(parseInt(hours), parseInt(minutes), 0, 0);
      
      return date.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      console.error('Error formatting time:', error, dateString);
      return dateString;
    }
  };

  // Helper function to check if event spans multiple days
  const isMultiDayEvent = (startDateTime, endDateTime) => {
    if (!startDateTime || !endDateTime) return false;
    
    try {
      // Extract date portions
      const startDate = startDateTime.includes('T') 
        ? startDateTime.split('T')[0] 
        : startDateTime.split(' ')[0];
      const endDate = endDateTime.includes('T') 
        ? endDateTime.split('T')[0] 
        : endDateTime.split(' ')[0];
      
      return startDate !== endDate;
    } catch (error) {
      console.error('Error checking multi-day event:', error);
      return false;
    }
  };

  // Helper function to format date range for multi-day events
  const formatDateRange = (startDateTime, endDateTime) => {
    if (!startDateTime) return '';
    
    try {
      const startFormatted = formatDate(startDateTime);
      
      if (!endDateTime || !isMultiDayEvent(startDateTime, endDateTime)) {
        return startFormatted;
      }
      
      const endFormatted = formatDate(endDateTime);
      return `${startFormatted} - ${endFormatted}`;
    } catch (error) {
      console.error('Error formatting date range:', error);
      return formatDate(startDateTime);
    }
  };

  // Helper function to format time range (handles multi-day differently)
  const formatTimeRange = (startDateTime, endDateTime) => {
    if (!startDateTime) return '';
    
    try {
      const startTime = formatTime(startDateTime);
      
      if (!endDateTime) {
        return startTime;
      }
      
      if (isMultiDayEvent(startDateTime, endDateTime)) {
        // For multi-day events, show time with date context
        const endTime = formatTime(endDateTime);
        return `${startTime} - ${endTime}`;
      } else {
        // For same-day events, show time range
        const endTime = formatTime(endDateTime);
        return `${startTime} - ${endTime}`;
      }
    } catch (error) {
      console.error('Error formatting time range:', error);
      return formatTime(startDateTime);
    }
  };

  const isEventPast = (eventDateTime) => {
    return new Date(eventDateTime) < new Date();
  };

  const getRSVPButtonStyle = (status, currentStatus) => {
    const isSelected = status === currentStatus;
    const baseClasses = "px-3 py-1 rounded-md text-sm font-medium transition-colors duration-200";
    
    if (status === 'attending') {
      return `${baseClasses} ${isSelected 
        ? 'bg-green-600 text-white' 
        : 'bg-green-100 text-green-700 hover:bg-green-200'}`;
    } else if (status === 'maybe') {
      return `${baseClasses} ${isSelected 
        ? 'bg-yellow-600 text-white' 
        : 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200'}`;
    } else {
      return `${baseClasses} ${isSelected 
        ? 'bg-red-600 text-white' 
        : 'bg-red-100 text-red-700 hover:bg-red-200'}`;
    }
  };

  const handleEditEvent = async (event) => {
    try {
      // Fetch the complete event data to ensure we have all fields
      const response = await intranetApi.events.getEvent(event.id);
      const fullEventData = response.data.event;
      
      setEditingEvent(fullEventData);
      setShowEditModal(true);
      setDropdownOpen({});
    } catch (error) {
      console.error('Error fetching event for edit:', error);
      // Fallback to using the list data if API call fails
      setEditingEvent(event);
      setShowEditModal(true);
      setDropdownOpen({});
    }
  };

  const handleEventUpdated = (updatedEvent) => {
    setEvents(prev => prev.map(event => 
      event.id === updatedEvent.id ? updatedEvent : event
    ));
    setShowEditModal(false);
    setEditingEvent(null);
  };

  const canUserEditEvent = (event) => {
    if (!currentUser) return false;
    // User can edit if they are the creator or an admin
    return event.created_by === currentUser.id || currentUser.role_type === 'admin';
  };

  const toggleDropdown = (eventId) => {
    setDropdownOpen(prev => ({
      ...prev,
      [eventId]: !prev[eventId]
    }));
  };

  const fetchEventAttendees = async (eventId, forceRefresh = false) => {
    if (!forceRefresh && eventAttendees[eventId]) return; // Already loaded

    setAttendeesLoading(prev => ({ ...prev, [eventId]: true }));
    
    try {
      const response = await intranetApi.events.getEventAttendees(eventId, 'attending');
      setEventAttendees(prev => ({
        ...prev,
        [eventId]: response.data.attendees || []
      }));
    } catch (error) {
      console.error('Error fetching attendees:', error);
    } finally {
      setAttendeesLoading(prev => ({ ...prev, [eventId]: false }));
    }
  };

  const toggleAttendeesList = async (eventId) => {
    const isCurrentlyShowing = showAttendees[eventId];
    
    setShowAttendees(prev => ({
      ...prev,
      [eventId]: !isCurrentlyShowing
    }));

    // Fetch attendees if showing and not already loaded
    if (!isCurrentlyShowing && !eventAttendees[eventId]) {
      await fetchEventAttendees(eventId);
    }
  };

  const renderEventCard = (event) => {
    const isPast = isEventPast(event.start_datetime);
    const eventType = eventTypes.find(type => type.id === event.event_type_id);
    const canEdit = canUserEditEvent(event);

    return (
      <div key={event.id} className="bg-white rounded-lg shadow border border-gray-200 overflow-hidden">
        <div className="p-6">
          {/* Header */}
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center space-x-3">
              <div 
                className="flex-shrink-0 h-12 w-12 rounded-lg flex items-center justify-center text-white"
                style={{ backgroundColor: event.event_type_color || eventType?.color || '#6B7280' }}
              >
                {(() => {
                  const IconComponent = getIconComponent(event.event_type_icon || eventType?.icon, 'event');
                  return <IconComponent className="h-6 w-6" />;
                })()}
              </div>
              <div>
                <h4 className="text-lg font-semibold text-gray-900">{event.title}</h4>
                <p className="text-sm text-gray-500">
                  {event.event_type_label || eventType?.display_label || 'Event'}
                </p>
              </div>
            </div>
            
            <div className="flex items-center space-x-2">
              {isPast && (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                  Past Event
                </span>
              )}
              
              {canEdit && (
                <div className="relative dropdown-container">
                  <button
                    onClick={() => toggleDropdown(event.id)}
                    className="p-1 text-gray-400 hover:text-gray-600 transition-colors duration-200"
                  >
                    <EllipsisVerticalIcon className="h-5 w-5" />
                  </button>
                  
                  {dropdownOpen[event.id] && (
                    <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg z-10 border border-gray-200">
                      <div className="py-1">
                        <button
                          onClick={() => handleEditEvent(event)}
                          className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors duration-200"
                        >
                          <PencilIcon className="h-4 w-4 mr-3" />
                          Edit Event
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Event Details */}
          <div className="space-y-3 mb-4">
            <div className="flex items-center text-sm text-gray-600">
              <CalendarIcon className="h-4 w-4 mr-2" />
              {formatDateRange(event.start_datetime, event.end_datetime)}
            </div>
            
            {event.start_datetime && (
              <div className="flex items-center text-sm text-gray-600">
                <ClockIcon className="h-4 w-4 mr-2" />
                {formatTimeRange(event.start_datetime, event.end_datetime)}
              </div>
            )}
            
            {event.location && (
              <div className="flex items-center text-sm text-gray-600">
                <MapPinIcon className="h-4 w-4 mr-2" />
                {event.location}
              </div>
            )}
          </div>

          {/* Description */}
          {event.description && (
            <div className="mb-4">
              <p className="text-gray-700 text-sm leading-relaxed">
                {event.description.length > 150 ? (
                  <>
                    {event.description.substring(0, 150)}...
                    <button className="text-indigo-600 hover:text-indigo-500 ml-1">
                      Read more
                    </button>
                  </>
                ) : (
                  event.description
                )}
              </p>
            </div>
          )}

          {/* RSVP Stats */}
          <div className="flex items-center space-x-6 mb-4 text-sm text-gray-500">
            <button
              onClick={() => toggleAttendeesList(event.id)}
              className="flex items-center hover:text-green-600 transition-colors duration-200 cursor-pointer"
              disabled={!event.attending_count || event.attending_count === 0}
            >
              <CheckCircleIcon className="h-4 w-4 mr-1 text-green-600" />
              {event.attending_count || 0} attending
              {event.attending_count > 0 && (
                <ChevronDownIcon className={`h-4 w-4 ml-1 transition-transform duration-200 ${
                  showAttendees[event.id] ? 'rotate-180' : ''
                }`} />
              )}
            </button>
            <div className="flex items-center">
              <QuestionMarkCircleIcon className="h-4 w-4 mr-1 text-yellow-600" />
              {event.maybe_count || 0} maybe
            </div>
            <div className="flex items-center">
              <XMarkIcon className="h-4 w-4 mr-1 text-red-600" />
              {event.declined_count || 0} declined
            </div>
            {event.max_attendees && (
              <div className="flex items-center">
                <UserGroupIcon className="h-4 w-4 mr-1" />
                Max: {event.max_attendees}
              </div>
            )}
          </div>

          {/* Attendees List */}
          {showAttendees[event.id] && (
            <div className="mb-4 p-4 bg-gray-50 rounded-lg border">
              <h5 className="text-sm font-medium text-gray-900 mb-3">
                Who's Attending ({event.attending_count || 0})
              </h5>
              
              {attendeesLoading[event.id] ? (
                <div className="flex justify-center py-4">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600"></div>
                </div>
              ) : eventAttendees[event.id] && eventAttendees[event.id].length > 0 ? (
                <div className="space-y-2">
                  {eventAttendees[event.id].map((attendee) => (
                    <div key={attendee.user_id} className="flex items-center space-x-3">
                      <div className="flex-shrink-0 h-8 w-8 bg-indigo-100 rounded-full flex items-center justify-center">
                        <span className="text-sm font-medium text-indigo-600">
                          {attendee.user_name ? attendee.user_name.charAt(0).toUpperCase() : '?'}
                        </span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {attendee.user_name || 'Unknown User'}
                        </p>
                        <p className="text-xs text-gray-500 truncate">
                          {attendee.user_email}
                        </p>
                      </div>
                      <div className="text-xs text-gray-400">
                        RSVP'd {new Date(attendee.rsvp_at).toLocaleDateString()}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-500 text-center py-4">
                  No attendees yet
                </p>
              )}
            </div>
          )}

          {/* RSVP Buttons */}
          {!isPast && (
            <div className="flex items-center space-x-2 pt-4 border-t border-gray-200">
              <span className="text-sm text-gray-500 mr-2">RSVP:</span>
              <button
                onClick={() => handleRSVP(event.id, 'attending')}
                disabled={rsvpLoading[event.id]}
                className={getRSVPButtonStyle('attending', event.user_rsvp_status)}
              >
                <CheckCircleIcon className="h-4 w-4 inline mr-1" />
                Attending
              </button>
              <button
                onClick={() => handleRSVP(event.id, 'maybe')}
                disabled={rsvpLoading[event.id]}
                className={getRSVPButtonStyle('maybe', event.user_rsvp_status)}
              >
                <QuestionMarkCircleIcon className="h-4 w-4 inline mr-1" />
                Maybe
              </button>
              <button
                onClick={() => handleRSVP(event.id, 'declined')}
                disabled={rsvpLoading[event.id]}
                className={getRSVPButtonStyle('declined', event.user_rsvp_status)}
              >
                <XMarkIcon className="h-4 w-4 inline mr-1" />
                Can't Attend
              </button>
              
              {rsvpLoading[event.id] && (
                <div className="ml-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-indigo-600"></div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Company Events</h2>
            <p className="mt-1 text-sm text-gray-500">
              View and manage company events, meetings, and activities
            </p>
          </div>
        </div>
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
          <p className="text-red-600 mb-4">{error}</p>
          <button 
            onClick={fetchEvents} 
            className="bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700 transition-colors duration-200"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Company Events</h2>
          <p className="mt-1 text-sm text-gray-500">
            View and manage company events, meetings, and activities
          </p>
        </div>
        <button 
          onClick={() => setShowCreateModal(true)}
          className="bg-indigo-600 text-white px-4 py-2 rounded-md hover:bg-indigo-700 transition-colors duration-200 flex items-center"
        >
          <PlusIcon className="h-5 w-5 mr-2" />
          Create Event
        </button>
      </div>

      {/* View Toggle */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setView('upcoming')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              view === 'upcoming'
                ? 'border-indigo-500 text-indigo-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <ClockIcon className="h-5 w-5 inline mr-2" />
            Upcoming Events
          </button>
          <button
            onClick={() => setView('all')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              view === 'all'
                ? 'border-indigo-500 text-indigo-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <CalendarIcon className="h-5 w-5 inline mr-2" />
            All Events
          </button>
        </nav>
      </div>

      {/* Events List */}
      {events.length === 0 ? (
        <div className="bg-white rounded-lg shadow border border-gray-200 p-8 text-center">
          <div className="max-w-md mx-auto">
            <div className="h-24 w-24 mx-auto bg-green-100 rounded-full flex items-center justify-center mb-4">
              <CalendarIcon className="h-12 w-12 text-green-600" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {view === 'upcoming' ? 'No Upcoming Events' : 'No Events Found'}
            </h3>
            <p className="text-gray-500 mb-6">
              {view === 'upcoming' 
                ? 'There are no upcoming events scheduled. Check back later or create a new event.'
                : 'No events have been created yet. Be the first to organize something!'
              }
            </p>
            <button
              onClick={() => setShowCreateModal(true)}
              className="bg-indigo-600 text-white px-4 py-2 rounded-md hover:bg-indigo-700 transition-colors duration-200 inline-flex items-center"
            >
              <PlusIcon className="h-5 w-5 mr-2" />
              Create First Event
            </button>
          </div>
        </div>
      ) : (
        <div className="space-y-6">
          {events.map(event => renderEventCard(event))}
        </div>
      )}

      {/* Create Event Modal */}
      {showCreateModal && (
        <CreateEvent
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          onEventCreated={(newEvent) => {
            // Add the new event to the events list
            setEvents(prev => [newEvent, ...prev]);
            setShowCreateModal(false);
          }}
        />
      )}

      {/* Edit Event Modal */}
      {showEditModal && editingEvent && (
        <CreateEvent
          isOpen={showEditModal}
          onClose={() => {
            setShowEditModal(false);
            setEditingEvent(null);
          }}
          onEventUpdated={handleEventUpdated}
          event={editingEvent}
        />
      )}
    </div>
  );
};

export default CompanyEvents; 